import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { RequestStatus } from 'rpc/model/squareup/customers/request';

const GENERIC_ERROR = {
  errors: [],
  status: RequestStatus.STATUS_INTERNAL_ERROR,
};

export const getResponseError = (
  response: {
    errors: readonly { details: string }[];
    status: RequestStatus;
  } = GENERIC_ERROR
) => {
  return {
    error: {
      data: {
        message:
          response.errors.length > 0
            ? response.errors.map((e) => e.details).join('; ')
            : 'Something went wrong.',
      },
      status: response.status,
    },
  };
};

export const callApi = <T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callFn: () => Promise<any>
): MaybePromise<QueryReturnValue<T>> => {
  return new Promise((res, rej) => {
    callFn()
      .then((result: T) => res({ data: result }))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((ex: any) =>
        rej(
          getResponseError({
            errors: [ex],
            status: RequestStatus.STATUS_INTERNAL_ERROR,
          })
        )
      );
  });
};

export const delay = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Returns true when an RTK Query error represents a backend
 * STATUS_PRECONDITION_FAILED response. The RPC envelope returns HTTP 200 with a
 * business-logic status, which RTK Query surfaces as the `status` field on the
 * FetchBaseQueryError. Use this to distinguish an actionable precondition
 * failure from a generic error.
 */
export const isPreconditionFailedError = (error: unknown): boolean => {
  if (error && typeof error === 'object' && 'data' in error) {
    const apiError = error as FetchBaseQueryError;
    return Number(apiError.status) === RequestStatus.STATUS_PRECONDITION_FAILED;
  }
  return false;
};
